import React, { useRef } from "react";
import { useScroll, useTransform, motion, MotionValue } from "motion/react";

export const ContainerScroll = ({
  titleComponent,
  children,
}: {
  titleComponent: string | React.ReactNode;
  children: React.ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
  });

  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();

    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const scaleDimensions = () => {
    return isMobile ? [0.82, 1] : [1.08, 1];
  };

  const rotate = useTransform(scrollYProgress, [0, 1], [18, 0]);

  const scale = useTransform(scrollYProgress, [0, 1], scaleDimensions());

  const translate = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      ref={containerRef}
      className="relative flex items-center justify-center h-[45rem] md:h-[62rem] lg:h-[68rem]"
    >
      <div
        className="relative w-full py-12 lg:py-28"
        style={{
          perspective: "1500px",
        }}
      >
        <Header translate={translate} titleComponent={titleComponent} />

        <Card rotate={rotate} translate={translate} scale={scale}>
          {children}
        </Card>
      </div>
    </section>
  );
};

export const Header = ({
  translate,
  titleComponent,
}: {
  translate: MotionValue<number>;
  titleComponent: React.ReactNode;
}) => {
  return (
    <motion.div
      style={{
        translateY: translate,
      }}
      className="
        relative
        z-20
        max-w-7xl
        mx-auto
        text-center
        px-4
        md:px-8
      "
    >
      {titleComponent}
    </motion.div>
  );
};

export const Card = ({
  rotate,
  scale,
  children,
}: {
  rotate: MotionValue<number>;
  scale: MotionValue<number>;
  translate: MotionValue<number>;
  children: React.ReactNode;
}) => {
  return (
    <motion.div
      style={{
        rotateX: rotate,
        scale,
        transformStyle: "preserve-3d",
        boxShadow:
          "0 10px 30px rgba(0,0,0,0.12), 0 40px 80px rgba(0,0,0,0.15), 0 80px 120px rgba(0,0,0,0.08)",
      }}
      className="
        relative
        z-10
        max-w-7xl
        mx-auto
        w-full
        -mt-28
        md:-mt-40
        lg:-mt-28
        border
        border-[#4CAF50]/20
        bg-[#111111]
        rounded-[36px]
        p-2
        md:p-4
        lg:p-5
        overflow-hidden
      "
    >
      {/* Premium Glow */}
      <div className="absolute inset-0 rounded-[36px] bg-gradient-to-br from-[#4CAF50]/10 via-transparent to-transparent pointer-events-none" />

      {/* Dashboard Container */}
      <div
        className="
          relative
          h-full
          w-full
          overflow-hidden
          rounded-[28px]
          bg-white
          dark:bg-zinc-900
        "
      >
        {children}
      </div>
    </motion.div>
  );
};
